import { useState, useEffect } from 'react';
import { fetchIVAnalysis } from '../utils/api';
import { getInterpretation } from '../utils/calculations';

export default function IVAnalysis({ selectedSymbol, selectedDate, onAnalysisLoaded }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selectedSymbol) {
      setAnalysis(null);
      setError(null);
      if (onAnalysisLoaded) onAnalysisLoaded(null);
      return;
    }

    setLoading(true);
    setError(null);

    fetchIVAnalysis(selectedSymbol, selectedDate || null)
      .then((data) => {
        setAnalysis(data);
        if (onAnalysisLoaded) onAnalysisLoaded(parseFloat(data.iv_percent));
      })
      .catch(() => {
        setError(`No IV data available for ${selectedSymbol}${selectedDate ? ` on ${selectedDate}` : ''}`);
        setAnalysis(null);
        if (onAnalysisLoaded) onAnalysisLoaded(null);
      })
      .finally(() => setLoading(false));
  }, [selectedSymbol, selectedDate, onAnalysisLoaded]);

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="grid grid-cols-3 gap-3">
          <div className="h-16 bg-neutral-100 dark:bg-white/5 rounded-clay-sm" />
          <div className="h-16 bg-neutral-100 dark:bg-white/5 rounded-clay-sm" />
          <div className="h-16 bg-neutral-100 dark:bg-white/5 rounded-clay-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="h-24 bg-neutral-100 dark:bg-white/5 rounded-clay-sm" />
          <div className="h-24 bg-neutral-100 dark:bg-white/5 rounded-clay-sm" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-clay-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/60 p-4">
        <p className="text-sm text-amber-700 dark:text-amber-400">{error}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
          Historical data is updated daily at 6:30 PM IST.
        </p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center py-6 text-neutral-400 dark:text-neutral-500 text-sm">
        Select a symbol to view IV analysis
      </div>
    );
  }

  const {
    trade_date,
    iv_percent,
    spot_price,
    atm_strike,
    ce_premium,
    pe_premium,
    ce_iv,
    pe_iv,
    days_to_expiry,
    expiry_date,
    iv_rank,
    iv_percentile,
    historical_stats,
  } = analysis;

  const interpretation = getInterpretation(iv_rank, iv_percentile);

  const rankColor =
    iv_rank > 70 ? 'bg-orange-500' : iv_rank < 30 ? 'bg-green-500' : 'bg-blue-500';

  const pctColor =
    iv_percentile > 70
      ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400 border-orange-200 dark:border-orange-800'
      : iv_percentile < 30
      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border-green-200 dark:border-green-800'
      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 border-blue-200 dark:border-blue-800';

  const formatDate = (dateStr) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });

  const formatShortDate = (dateStr) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short',
    });

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">IV Analysis</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">Data from {formatDate(trade_date)}</span>
      </div>

      {/* Row 1: IV | IV Rank | IV Percentile */}
      <div className="grid grid-cols-3 gap-3">
        {/* Current IV */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 p-3 text-center">
          <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">Implied Volatility</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-50">{iv_percent}%</p>
          <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 space-x-1">
            <span>Spot ₹{parseFloat(spot_price).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
            <span>·</span>
            <span>ATM ₹{parseFloat(atm_strike).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
          </div>
        </div>

        {/* IV Rank */}
        <div className="rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-3 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">IV Rank</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-50">
            {iv_rank !== null ? iv_rank : '—'}
          </p>
          {iv_rank !== null && (
            <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${rankColor}`} style={{ width: `${iv_rank}%` }} />
            </div>
          )}
        </div>

        {/* IV Percentile */}
        <div className={`rounded-lg border p-3 text-center ${pctColor}`}>
          <p className="text-xs opacity-75 mb-1">IV Percentile</p>
          <p className="text-2xl font-semibold">
            {iv_percentile !== null ? `${iv_percentile}th` : '—'}
          </p>
        </div>
      </div>

      {/* Row 2: Interpretation + Historical Stats | Option Details */}
      <div className="grid grid-cols-2 gap-3 items-stretch">
        {/* Left col: interpretation + historical stats */}
        <div className="space-y-2">
          {interpretation && (
            <div className="rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-3">
              <p className={`text-sm font-medium ${interpretation.color}`}>{interpretation.text}</p>
              {interpretation.recommendation && (
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">
                  {interpretation.recommendation}
                </p>
              )}
            </div>
          )}
          {historical_stats && historical_stats.data_points > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                Historical Stats ({historical_stats.data_points} days)
              </p>
              <div className="grid grid-cols-3 gap-1.5 text-center">
                {[
                  { label: 'Min IV', value: historical_stats.min_iv },
                  { label: 'Avg IV', value: historical_stats.avg_iv },
                  { label: 'Max IV', value: historical_stats.max_iv },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {value != null ? `${value.toFixed(2)}%` : '—'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right col: Option Details 3×2 grid */}
        <div className="flex flex-col justify-end">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            Option Details
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { label: 'CE Premium', value: `₹${parseFloat(ce_premium).toFixed(2)}` },
              { label: 'PE Premium', value: `₹${parseFloat(pe_premium).toFixed(2)}` },
              { label: 'Days to Exp', value: days_to_expiry },
              { label: 'CE IV', value: ce_iv ? `${(parseFloat(ce_iv) * 100).toFixed(1)}%` : 'N/A' },
              { label: 'PE IV', value: pe_iv ? `${(parseFloat(pe_iv) * 100).toFixed(1)}%` : 'N/A' },
              { label: 'Expiry', value: formatShortDate(expiry_date) },
            ].map(({ label, value }) => (
              <div key={label} className="rounded bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
